import React from 'react';
import { motion, easeInOut, easeOut } from 'framer-motion';
import { User, CheckCircle, MapPin, Calendar, Mail, AtSign, Activity, ContactRound, PhoneMissed, PhoneCall } from 'lucide-react';
import { User as UserType } from '../types/user';

interface UserExistsCardProps {
  user: UserType;
}

export const UserExistsCard: React.FC<UserExistsCardProps> = ({ user }) => {
  const containerVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: easeOut,
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.5 }
    }
  };

  const pulseVariants = {
    pulse: {
      scale: [1, 1.05, 1],
      transition: {
        duration: 2,
        repeat: Infinity,
        ease: easeInOut
      }
    }
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="max-w-2xl mx-auto"
    >
      <motion.div
        variants={pulseVariants}
        animate="pulse"
        className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-2xl p-8 shadow-2xl"
      >
        <motion.div
          variants={itemVariants}
          className="flex items-center justify-center mb-6"
        >
          <div className="relative">
            <div className="w-20 h-20 bg-gradient-to-br from-green-400 to-emerald-600 rounded-full flex items-center justify-center">
              <User className="w-10 h-10 text-white" />
            </div>
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.3, type: "spring", stiffness: 300 }}
              className="absolute -top-2 -right-2 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center"
            >
              <CheckCircle className="w-5 h-5 text-white" />
            </motion.div>
          </div>
        </motion.div>

        <motion.h2
          variants={itemVariants}
          className="text-3xl font-bold text-gray-800 text-center mb-2"
        >
          User Found!
        </motion.h2>

        <motion.p
          variants={itemVariants}
          className="text-gray-600 text-center mb-8"
        >
          Here are the details for this user
        </motion.p>

        <div className="space-y-6">
          <motion.div
            variants={itemVariants}
            className="bg-white rounded-xl p-6 shadow-lg border border-gray-100"
          >
            <h3 className="text-xl font-semibold text-gray-800 mb-4">User Information</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <User className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Full Name</p>
                  <p className="font-medium text-gray-800">{user.name}</p>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                  <ContactRound className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Registered</p>
                  <p className="font-medium text-gray-800">@{user.form}</p>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                  <Mail className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Email</p>
                  <p className="font-medium text-xs text-gray-800">{user.email}</p>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                  <PhoneCall className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Phone</p>
                  <p className="font-medium text-xs text-gray-800">{user.phone}</p>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Join Date</p>
                  <p className="font-medium text-gray-800">{new Date(user.joinDate).toLocaleDateString()}</p>
                </div>
              </div>

              {user.location && (
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-red-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Location</p>
                    <p className="font-medium text-gray-800">{user.location}</p>
                  </div>
                </div>
              )}

              <div className="flex items-center space-x-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  user.status === 'active' ? 'bg-green-100' : 'bg-gray-100'
                }`}>
                  <Activity className={`w-5 h-5 ${
                    user.status === 'active' ? 'text-green-600' : 'text-gray-600'
                  }`} />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Status</p>
                  <p className={`font-medium capitalize ${
                    user.status === 'active' ? 'text-green-600' : 'text-gray-600'
                  }`}>
                    {user.status}
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {user.bio && (
            <motion.div
              variants={itemVariants}
              className="bg-white rounded-xl p-6 shadow-lg border border-gray-100"
            >
              <h3 className="text-lg font-semibold text-gray-800 mb-3">Bio</h3>
              <p className="text-gray-600 leading-relaxed">{user.bio}</p>
            </motion.div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};